/**
 * Multi-tab session restore: wait for persist caches, skip duplicate login, ignore transient Firebase null.
 */

import type { Auth } from 'firebase/auth';
import type { User } from '@/types/user.types';

/** Persist API subset used to wait for localStorage rehydration. */
type PersistHydrationApi = {
  persist: {
    hasHydrated: () => boolean;
    onFinishHydration: (fn: () => void) => () => void;
  };
};

/** Pause before treating a Firebase `null` user as a real sign-out. */
const AUTH_NULL_CONFIRM_MS = 400;

/** Resolve when one persisted store has finished reading localStorage. */
export function waitForStoreHydration(
  store: PersistHydrationApi,
  timeoutMs = 2_000,
): Promise<void> {
  if (store.persist.hasHydrated()) return Promise.resolve();

  return new Promise((resolve) => {
    let settled = false;
    const finish = (): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      unsub();
      resolve();
    };
    const unsub = store.persist.onFinishHydration(finish);
    const timer = setTimeout(finish, timeoutMs);
  });
}

/** True when the in-memory staff session already belongs to this Firebase uid. */
export function isSameFirebaseSession(user: User | null, firebaseUid: string): boolean {
  return Boolean(user?.firebaseUid && user.firebaseUid === firebaseUid);
}

/** True when persist has a user + roles for this Firebase uid (enough to skip GET /users/login). */
export function canRestoreStaffSession(
  user: User | null,
  roles: { length: number },
  firebaseUid: string,
): boolean {
  return isSameFirebaseSession(user, firebaseUid) && roles.length > 0;
}

/** True only after IndexedDB auth is ready and `currentUser` stays null. */
export async function confirmFirebaseSignedOut(
  authInstance: Auth,
  delayMs = AUTH_NULL_CONFIRM_MS,
): Promise<boolean> {
  await authInstance.authStateReady();
  if (authInstance.currentUser) return false;
  await new Promise<void>((resolve) => {
    setTimeout(resolve, delayMs);
  });
  return authInstance.currentUser === null;
}
